import { cloneDeep } from "lodash-es";

/** Returns true if s is enum of type T. NOTE: works only with string enums, not number-based enums!
 * Usage: isSomeEnum(MyEnumType)(enumObject)
 * Example: isSomeEnum(ModelVisibility)(visibility)
 * 
 * Source: https://stackoverflow.com/a/58278753
 */
export const isSomeEnum = <T extends { [s: string]: unknown }>(e: T) => (token: unknown): token is T[keyof T] => Object.values(e).includes(token as T[keyof T]);

/** Attempts to parse a string as a specific enum. Returns undefined if parsing fails.
 * 
 * Source: https://stackoverflow.com/a/41548441
 */
export const enumFromStringValue = <T>(enm: { [s: string]: T }, stringValue: string): T | undefined => {
    return (Object.values(enm) as unknown as string[]).includes(stringValue)
        ? stringValue as unknown as T
        : undefined;
}

/** Attemps to parse an enum from a string. Returns that enum, or a specified default enum if parsing failed. */
export const enumFromStringOrDefault = <T>(enm: { [s: string]: T }, stringValue: string, defaultValue: T): T => {
    const parsedValue = enumFromStringValue(enm, stringValue);
    if (parsedValue) {
        return parsedValue;
    }

    return defaultValue;
}

/**
 * Sorts an array of enum values based on the order of their declaration in the enum type. Returns a new
 * copy of the array.
 *
 * @template T - The type of the enum.
 * @param enumType - The enum type to use for determining the order of values.
 * @param enumArray - The array of enum values to be sorted.
 * @returns A new array of enum values sorted in the order they are declared in the enum type.
 *
 * @example
 * enum Colors {
 *   Red,
 *   Green,
 *   Blue
 * }
 * 
 * const unsortedColors = [Colors.Blue, Colors.Red, Colors.Green];
 * const sortedColors = sortEnumsInIndexOrder(Colors, unsortedColors);
 * console.log(sortedColors); // Output: [Colors.Red, Colors.Green, Colors.Blue]
 */
export const sortEnumsInIndexOrder = <T extends { [s: string]: unknown }>(enumType: T, enumArray: T[keyof T][]): T[keyof T][] => {
    const enumOrder = Object.values(enumType);
    return cloneDeep(enumArray).sort((a, b) => enumOrder.indexOf(a) - enumOrder.indexOf(b));
}
